import { Injectable, NgZone } from '@angular/core';
import { Mutex } from 'async-mutex';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  firstValueFrom,
  fromEvent,
  map,
  Observable,
  switchMap,
  takeWhile,
  catchError,
  of,
} from 'rxjs';
import { cloneDeep, isEmpty } from 'lodash';
import { captureException } from '@sentry/angular';
// Krisp noise filter is dynamically loaded via DynamicModuleLoaderService
import type { KrispNoiseFilterProcessor } from '@livekit/krisp-noise-filter/dist/NoiseFilterProcessor';
import {
  Room,
  LocalParticipant,
  RoomEvent,
  ConnectionState,
  DisconnectReason,
  Track,
  RemoteTrackPublication,
  VideoQuality,
  Participant,
  RemoteParticipant,
  ConnectionQuality,
  LocalTrackPublication,
  LocalAudioTrack,
  TrackPublication,
  VideoCodec,
  ScalabilityMode,
  VideoEncoding,
  VideoPreset,
} from 'livekit-client';
import { TranslateService } from '@ngx-translate/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Collaboration } from '@pncl/common-models';
import {
  ActiveRTCDevices,
  AgentInfo,
  CallContext,
  CallError,
  CallParticipant,
  CallProvider,
  HIDE_TRACK_SYMBOL,
  ParticipantEventAction,
  RTCCloudRecording,
  RTCInterface,
  RTCRoomCreationOpts,
  RoomAuth,
  ScreenShareOptions,
  SubscribeCallQuality,
  TrackEventAction,
  TrackState,
  TrackType,
} from '../common/interfaces/rtc-interface';
import { SpaceRepository } from '../state/space.repository';
import { Session } from '../models/session';
import { DEFAULT_DEVICE_ID } from '../common/utils/devices-handle-util';
import { UserSystemStatus } from '../utilities/webrtc-stats-reporter/webrtc-stats-reporter-types';
import { InterceptorSkipHeader } from '../auth.interceptor';
import { modifiedSetTimeout } from '../utilities/ZoneUtils';
import { ErrorInterceptorSkipHeader } from '../common/functions-and-constant/interceptor';
import { DEFAULT_VIDEO_STREAM_CODEC } from '../models/device-manger';
import { UserService } from './user.service';
import { CustomerSupportService } from './customer-support.service';
import { ProviderStateService } from './provider-state.service';
import { SpacesService } from './spaces.service';
import { FLAGS, FlagsService } from './flags.service';
import { NoiseCancellationConfigurationService } from './noise-cancellation-configuration.service';
import { TelemetryService } from './telemetry.service';
import { NotificationToasterService } from './notification-toaster.service';
import { SpaceRecordingService } from './space-recording.service';
import { DeviceAndBrowserDetectorService } from './device-and-browser-detector.service';
import { DynamicModuleLoaderService, DynamicModuleKey } from './dynamic-module-loader.service';
import { ObserverModeService } from './observer-mode.service';
import { AiSessionService } from './ai-session.service';
import { isObserverParticipant } from './observer-mode.utils';

export enum PendingSubscription {
  SUBSCRIBE,
  UNSUBSCRIBE,
}

const SERVERURL = 'wss://pencil.livekit.cloud';
const LIVEKIT_HOST = 'https://pencil.livekit.cloud';
const LIVEKIT_EGRESS_PATH = '/twirp/livekit.Egress/ListEgress';
const RECORDING_STATUS_CHECK_DELAY = 10000; // 10 seconds
const PENCIL_BOT_IDENTITY = 'pencil-bot';
const HIDDEN_PENCIL_BOT_IDENTITY = 'pencil-bot-hidden';
// LiveKit attribute an avatar "delegate" participant carries, pointing at the
// agent identity it publishes media on behalf of (its "principal"). The avatar
// plugins (Tavus / bitHuman cloud) run the avatar OUT OF PROCESS: it joins as a
// separate kind=agent participant that renders the video, while the real agent
// (principal) runs the brain and broadcasts PTT state. Both are kind=agent, so
// the FE would otherwise show two "Sparky" tiles — one with video (delegate),
// one without (principal). We collapse them: hide the principal's media-less
// tile, let the delegate be the single agent tile, and keep PTT reading from
// the principal. Value matches livekit-agents' ATTRIBUTE_PUBLISH_ON_BEHALF.
const ATTRIBUTE_PUBLISH_ON_BEHALF = 'lk.publish_on_behalf';

// LiveKit attribute carrying the agent's live lifecycle state, published by the
// agent framework itself (livekit-agents `room_io` sets it on every state
// change): `initializing | listening | thinking | speaking`. It is already on the
// wire for every call — reading it needs NO worker and NO LiveKit server change.
// Used to drive the agent's orb when no live video avatar is attached.
// Docs: https://docs.livekit.io/agents/build/state/
const ATTRIBUTE_AGENT_STATE = 'lk.agent.state';

enum RecordingStatusSource {
  LIVEKIT = 'livekit',
  PENCIL_BOT = 'pencil-bot',
}

@Injectable({
  providedIn: 'root',
})
export class LivekitService extends RTCInterface implements RTCCloudRecording {
  readonly provider = CallProvider.LIVEKIT;

  private room!: Room;
  private roomAuth: RoomAuth | null = null;
  private joinLeaveMutex = new Mutex();
  private startOrStopRecordingMutex = new Mutex();
  private currentActiveEgressRequest?: string;
  private krispProcessor?: KrispNoiseFilterProcessor;
  private enableNoiseCancellationMutex = new Mutex();
  private startRecordingRetryTimeout?: NodeJS.Timer;
  private startRecordingRetryAttempt = 0;
  private readonly MAX_START_RECORDING_RETRIES = 5;
  private readonly INITIAL_RETRY_DELAY = 1000; // 1 second
  private activeRecordingSources = new Set<RecordingStatusSource>();

  public currentLocalParticipant?: LocalParticipant;

  // Push-to-talk: the agent participant broadcasts its PTT state (mode + current
  // holder) via participant attributes. We mirror the latest agent attributes
  // here so the PTT button can react (show/hide by mode, lock when someone else
  // holds the floor). Emits raw `participant.attributes`; the consumer
  // (PttButtonStateService) re-enters the Angular zone + interprets the keys.
  private readonly _agentAttributes$ = new BehaviorSubject<Record<string, string>>({});
  readonly agentAttributes$ = this._agentAttributes$.asObservable();

  // The agent's raw `lk.agent.state` off the wire ('' when it hasn't published one
  // yet). Deliberately RAW: this service's job is to mirror what the room says, and
  // the presentation mapping (which states collapse together) belongs to whatever is
  // drawing. Rides `_agentAttributes$`, so it is already scoped to the PRINCIPAL
  // agent — an avatar delegate never clobbers it.
  readonly agentState$: Observable<string> = this._agentAttributes$.pipe(
    map((attrs) => attrs?.[ATTRIBUTE_AGENT_STATE] ?? ''),
    distinctUntilChanged(),
  );

  // Whether the voice agent (kind=AGENT principal, not the avatar delegate) is
  // currently IN the call — the source of truth for the AI-panel call-join buttons
  // (green when absent, red when present; CALL_JOIN_BUTTONS_SPEC.md). Derived from the
  // live room roster via getAgentParticipant(), recomputed on every participant
  // join/leave, so it's automatically consistent across all users and self-heals if a
  // dispatch silently fails. NOT Pencil presence (which tracks humans).
  private readonly _agentInCall$ = new BehaviorSubject<boolean>(false);
  readonly agentInCall$ = this._agentInCall$.asObservable();

  // Whether a live video AVATAR (Tavus/bitHuman cloud) is attached to this call —
  // i.e. an out-of-process delegate participant publishing on the principal's
  // behalf. This is the runtime shadow of the per-institution
  // `agentOrchestrator.avatarMode` opt-in: when the institution has it on, the
  // worker attaches the avatar and its delegate joins; when off, no delegate ever
  // appears. So the agent's face is decided by exactly one thing — is a delegate
  // here? Avatar if yes, orb if no. Recomputed on every participant event
  // alongside agentInCall$.
  private readonly _agentAvatarPresent$ = new BehaviorSubject<boolean>(false);
  readonly agentAvatarPresent$ = this._agentAvatarPresent$.asObservable();

  onEndScreenSharing?: () => Promise<void>;

  // Hold any early subscription is made before remote track publication is sent
  // participantIdWithTrackSource = participantId (sid on LiveKit) + Source (Camera, Mic, ....)
  private pendingSubscriptions: { [participantIdWithTrackSource: string]: PendingSubscription } =
    {};

  private egressCheckAttempts = 0;
  private readonly MAX_EGRESS_CHECKS = 3;

  private recordingStatusCheckTimeout?: NodeJS.Timer;

  constructor(
    public customerSupportService: CustomerSupportService,
    private userService: UserService,
    private spacesService: SpacesService,
    private ngZone: NgZone,
    private spaceRepo: SpaceRepository,
    public flagsService: FlagsService,
    noiseCancellationConfigurationService: NoiseCancellationConfigurationService,
    telemetry: TelemetryService,
    translateService: TranslateService,
    notificationToasterService: NotificationToasterService,
    providerState: ProviderStateService,
    private http: HttpClient,
    private spaceRecordingService: SpaceRecordingService,
    deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    private dynamicModuleLoader: DynamicModuleLoaderService,
    private observerModeService: ObserverModeService,
    private aiSessionService: AiSessionService,
  ) {
    super(
      flagsService,
      noiseCancellationConfigurationService,
      telemetry,
      translateService,
      notificationToasterService,
      providerState,
      customerSupportService,
      deviceAndBrowserDetectorService,
    );
    ngZone.runOutsideAngular(() => {
      // creates a new room with options
      this.room = new Room({
        // Manage updating stream quality based on current video width on our end
        // As LiveKit is super optimized & handling if the video element is being PIP, they will stop the stream
        adaptiveStream: false,

        // optimize publishing bandwidth and CPU for simulcasted tracks
        dynacast: true,
        disconnectOnPageLeave: false,
      });

      // Will be remove once having a stable LiveKit on prod
      Object.values(RoomEvent).forEach((state) => {
        if (
          [RoomEvent.ActiveSpeakersChanged, RoomEvent.ParticipantMetadataChanged].includes(state)
        ) {
          return;
        }
        this.room.on(state, (...args: any[]) => {
          console.log('Room Event', state, args);
        });
      });

      this.updateMetadata$().subscribe((metadata) => {
        this.setMetadata(metadata);
      });

      /**
       * Ignored TrackStreamStatedChanged event as our logic doesn't depend on handling this event
       * Ignored TrackPublished event as it just fires when a remote participant enables his cam or mic or starts a screensharing
       * but it doesn't have any information about the tracks itself which will come after with TrackSubscribed event. We can use
       * this event if we have a loading before showing the video (For example)
       *  */
      this.room
        // Local participant events
        .on(RoomEvent.ConnectionStateChanged, this.handleConnectionStateChanged.bind(this))
        .on(RoomEvent.Connected, this.handleCallIsConnected.bind(this))
        .on(RoomEvent.Reconnecting, this.handleReconnectingEvent.bind(this))
        .on(RoomEvent.SignalReconnecting, this.handleReconnectingEvent.bind(this))
        .on(RoomEvent.Reconnected, this.handleReconnectedEvent.bind(this))
        .on(RoomEvent.ConnectionQualityChanged, this.handleConnectionQualityChanged.bind(this))
        .on(RoomEvent.Disconnected, this.handleDisconnectedEvent.bind(this))
        .on(RoomEvent.LocalTrackPublished, this.handleLocalTrackEvent.bind(this))
        .on(RoomEvent.LocalTrackUnpublished, this.handleLocalTrackUnpubEvent.bind(this))

        // Remote participants events
        .on(RoomEvent.ParticipantDisconnected, this.handleParticipantLeaveEvent.bind(this))
        .on(RoomEvent.ParticipantConnected, this.handleParticipantJoinEvent.bind(this))
        // Push-to-talk: when the agent (re)joins it may already have a PTT mode
        // set — seed the FE from its current attributes so the button doesn't
        // wait for the next change event.
        .on(RoomEvent.ParticipantConnected, (p: RemoteParticipant) => {
          // Only the principal agent broadcasts PTT state; the avatar delegate
          // carries no PTT attrs, so ignore it (it would clobber the button).
          if (p.isAgent && !p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF]) {
            this._agentAttributes$.next({ ...p.attributes });
          }
        })
        // Keep the call-join button state (agentInCall$) in sync on any participant
        // change. Read straight from the room roster (not the flag-gated tile
        // handlers), so it's correct regardless of ENABLE_AGENT_JOIN_CALL.
        .on(RoomEvent.ParticipantConnected, () => this.recomputeAgentInCall())
        .on(RoomEvent.ParticipantDisconnected, () => this.recomputeAgentInCall())

        // Track events
        // 1st stage: Publication events
        .on(RoomEvent.TrackPublished, this.handleTrackPubEvent.bind(this))
        .on(RoomEvent.TrackUnpublished, this.handleTrackUnpubEvent.bind(this))
        // 2nd stage: Subscription events
        .on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubEvent.bind(this))
        .on(RoomEvent.TrackSubscribed, this.handleTrackSubscribedEvent.bind(this))
        // 3rd stage: Muted/Unmuted events
        .on(RoomEvent.TrackMuted, this.handleTrackMutedEvent.bind(this))
        .on(RoomEvent.TrackUnmuted, this.handleTrackUnMutedEvent.bind(this))

        // Recording handling events
        .on(RoomEvent.ParticipantMetadataChanged, this.handleParticipantMetadataChange.bind(this))
        // Push-to-talk: surface the agent's broadcast PTT state to the FE button.
        .on(
          RoomEvent.ParticipantAttributesChanged,
          this.handleParticipantAttributesChange.bind(this),
        )
        .on(RoomEvent.RecordingStatusChanged, (recording: boolean) =>
          this.handleRecordingStatusChange(recording, RecordingStatusSource.LIVEKIT),
        );
    });
  }

  // Added Reconnecting & SignalReconnecting state to the check as it still means call is connected
  // Also video call component is depending on its value for showing/hiding the entire component
  isConnected(): boolean {
    return [
      ConnectionState.Connected,
      ConnectionState.Reconnecting,
      ConnectionState.SignalReconnecting,
    ].includes(this.room.state);
  }

  isConnecting(): boolean {
    return this.room.state === ConnectionState.Connecting;
  }

  isDisconnecting(): boolean {
    return this.providerState.callDisconnecting;
  }

  /**
   * Load LiveKit auth for a space/room and prepare for joining a call.
   *
   * Behavior:
   * - If the current user is a host and automatic cloud recording is NOT enabled,
   *   force room creation by overriding `opts.skipRoomCreation = false`.
   *
   */
  async loadAuth(
    spaceId: string,
    breakoutRoomId: string | undefined,
    context: CallContext,
    isExtendExp = false,
    opts: RTCRoomCreationOpts = {
      skipRoomCreation: true,
      skipGenerateToken: false,
    },
  ): Promise<void> {
    // if we already have auth data for this room, do nothing
    if (!isExtendExp && this.isAuthValid(this.roomAuth, spaceId, breakoutRoomId)) {
      return;
    }

    // Generate a unique request ID for the current request
    const requestId = ++this.loadAuthCurrentRequestId;

    // Determine options taking into account host and automatic cloud recording settings
    if (
      this.spaceRepo.isCurrentUserHost() &&
      !this.spaceRecordingService.isAutomaticCloudRecordingEnabled
    ) {
      opts.skipRoomCreation = false;
    }

    if (!opts?.skipGenerateToken) {
      // reset roomAuth to make sure that it will not be used under any race condition
      // before the new one is created
      this.roomAuth = null;
    }

    const isObserver = this.observerModeService.isObserverModeActive();

    const userId: string | undefined = this.userService.user.getValue()?.user._id;
    if (userId) {
      const res = await firstValueFrom(
        this.spacesService.getVideoCallAuth(
          spaceId,
          breakoutRoomId,
          context,
          CallProvider.LIVEKIT,
          opts,
          isObserver,
        ),
      );

      if (!opts?.skipGenerateToken && requestId === this.loadAuthCurrentRequestId) {
        // set the room auth only if the request not being cancelled by another request

        this.roomAuth = (res?.body as any)?.data || null;
      }
    }
    if (!this.roomAuth || !this.roomAuth.token) {
      throw new Error('Could not get room data for LiveKit RTC');
    } else {
      this.providerState.callReady = true;
    }
  }

  protected constructRoomIdInRoomAuth(spaceId: string, breakoutRoomId?: string): string {
    if (!breakoutRoomId || Session.isMainRoom(breakoutRoomId, spaceId, true)) {
      return `${spaceId}-MainRoom`;
    } else {
      return `${spaceId}-${breakoutRoomId}`;
    }
  }

  getAuthData(): RoomAuth | null {
    return this.roomAuth;
  }

  setAuthData(data: RoomAuth | null): void {
    this.roomAuth = data;
    this.providerState.callReady = this.roomAuth ? true : false;
  }

  clearRoomAuthToken(): void {
    this.roomAuth = null;
  }

  doesAuthTokenExist(): boolean {
    return !!this.roomAuth && !!this.roomAuth.token;
  }

  async join(): Promise<void> {
    await this.ngZone.runOutsideAngular(async () => {
      await this.joinLeaveMutex.runExclusive(async () => {
        try {
          if (this.isConnected() || !this.roomAuth || !this.roomAuth.token) {
            return;
          }
          await this.room.connect(SERVERURL, this.roomAuth.token, {
            autoSubscribe: false,
          });
        } catch (joinCallError) {
          const joinCallReqStatusCode = ({ joinCallError } as any)?.joinCallError?.status;

          const isTokenExpired = joinCallReqStatusCode === 401;
          const isRoomNotExist = joinCallReqStatusCode === 404;

          if (!isTokenExpired && !isRoomNotExist) {
            captureException('[LiveKit] Connect to call failed', {
              extra: {
                error: joinCallError,
              },
            });
            this.handleDisconnectedEvent(DisconnectReason.JOIN_FAILURE);
            return;
          }

          if (!this.spaceRepo.activeSpaceId) {
            captureException('[LiveKit] No active space ID for room/auth creation');
            return;
          }

          try {
            await this.loadAuth(
              this.spaceRepo.activeSpaceId,
              this.spaceRepo.activeSpaceCurrentRoomUid,
              CallContext.SESSION,
              true,
              {
                skipRoomCreation: false,
                skipGenerateToken: isTokenExpired ? false : true,
              },
            );
          } catch (error) {
            this.logMeetingEvent('loadAuth error', {
              loadAuthError: error instanceof Error ? error.message : error,
            });
            this.handleDisconnectedEvent(DisconnectReason.JOIN_FAILURE);
            return;
          }

          this.joinLeaveMutex.cancel();
          this.join();
        }
      });
    });
  }

  async leave(): Promise<void> {
    await this.joinLeaveMutex.runExclusive(async () => {
      super.leave();
      if (!this.isConnected()) {
        return;
      }
      this.providerState.callDisconnecting = true;
      try {
        await this.room.disconnect();
      } catch (error) {
        /* empty catch block */
      } finally {
        this.providerState.callDisconnecting = false;
      }
    });
  }

  async publishAndStartBroadcastingVideoStream(mediaStreamTrack: MediaStreamTrack): Promise<void> {
    if (await this.reuseExistingLocalTrackPub(Track.Source.Camera, mediaStreamTrack)) {
      return;
    }

    await this.room.localParticipant.publishTrack(mediaStreamTrack, {
      simulcast: true,
      source: Track.Source.Camera,
      videoCodec: this.videoCodec,
      scalabilityMode: this.videoScalabilityMode,
      videoEncoding: this.videoEncoding,
      videoSimulcastLayers: this.videoSimulcastLayers,
    });
  }

  /** ***************** LocalVideo Track Publish Settings ****************************/

  get videoCodec(): VideoCodec {
    return (this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]?.video_codec ||
      DEFAULT_VIDEO_STREAM_CODEC) as VideoCodec;
  }

  get videoScalabilityMode(): ScalabilityMode | undefined {
    return this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]?.scalability_mode as
      | ScalabilityMode
      | undefined;
  }

  get videoEncoding(): VideoEncoding | undefined {
    return this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]
      ?.video_encoding as any as VideoEncoding | undefined;
  }

  get videoSimulcastLayers(): VideoPreset[] | undefined {
    const layer = this.flagsService.featureFlagsVariables[FLAGS.VIDEO_TRACK_SETTINGS]
      ?.video_simulcast_layer as any;
    if (!layer) {
      return undefined;
    }
    return [
      new VideoPreset(
        layer.width,
        layer.height,
        layer.maxBitrate,
        layer.maxFramerate,
        layer.priority,
      ),
    ];
  }

  /** ***************** End LocalVideo Track Publish Settings ****************************/

  private async publishAndEnableBroadcastingAudioStream(
    mediaStreamTrack: MediaStreamTrack,
  ): Promise<void> {
    if (await this.reuseExistingLocalTrackPub(Track.Source.Microphone, mediaStreamTrack)) {
      return;
    }

    await this.room.localParticipant.publishTrack(mediaStreamTrack, {
      source: Track.Source.Microphone,
    });
  }

  async enableNoiseCancellation(): Promise<void> {
    // Added a mutex here because the LocalTrackPublished event sometimes fires
    // twice simultaneously. The mutex prevents calling setEnabled/setProcessor simultaneously
    return this.enableNoiseCancellationMutex.runExclusive(async () => {
      // Music mode takes precedence — NC must not run while music mode is active
      if (this.isMusicModeEnabled) {
        return;
      }

      const localAudioTrackPub = this.room.localParticipant.getTrackPublication(
        Track.Source.Microphone,
      );
      const localAudioTrack = localAudioTrackPub?.track;

      // Check support first (this will load the module if needed)
      const isSupported = await this.checkKrispSupportAsync();

      if (!isSupported) {
        console.warn('[Livekit] Krisp noise cancellation is not supported on this browser');
        return;
      }

      if (!this.isNoiseCancellationSupported() || !localAudioTrack) {
        return;
      }

      try {
        if (!this.krispProcessor) {
          // Load the Krisp module dynamically
          const krispModule = await this.loadVideoCallsModules();

          if (!krispModule) {
            console.warn('[Livekit] Failed to load Krisp noise filter module');
            return;
          }

          this.krispProcessor = krispModule.KrispNoiseFilter();
          await localAudioTrack.setProcessor(this.krispProcessor as any);
        } else {
          console.log('[Livekit] Noise cancellation processor is already set, enabling it');
          await this.krispProcessor.setEnabled(true);
        }

        this.telemetry.event('[Livekit Noise Cancellation]', {
          action: 'enabled',
        });
      } catch (error) {
        captureException(new Error('Could not enable noise cancellation'), {
          extra: error,
        });
        return;
      }

      if (!this.room.localParticipant.isMicrophoneEnabled) {
        return;
      }

      this.rebroadcastLocalTrack(localAudioTrackPub);
    });
  }

  async disableNoiseCancellation(): Promise<void> {
    const localAudioTrackPub = this.room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );
    const localAudioTrack = localAudioTrackPub?.track;
    if (!this.isNoiseCancellationSupported() || !localAudioTrackPub || !localAudioTrack) {
      return;
    }

    await this.krispProcessor?.setEnabled(false);

    if (!this.room.localParticipant.isMicrophoneEnabled) {
      return;
    }

    this.rebroadcastLocalTrack(localAudioTrackPub);
  }

  async enableMusicMode(): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    if (this.isNoiseCancellationEnabled) {
      await this.disableNoiseCancellation();
    }

    this.telemetry.event('[Livekit Music Mode]', { action: 'enabled' });
  }

  async disableMusicMode(): Promise<void> {
    if (!this.isConnected()) {
      return;
    }

    if (this.isNoiseCancellationEnabled) {
      await this.enableNoiseCancellation();
    }

    this.telemetry.event('[Livekit Music Mode]', { action: 'disabled' });
  }

  async startBroadcastingVideoStream(): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Camera,
    );

    if (!localTrackPublication) {
      return;
    }

    await localTrackPublication.unmute();
  }

  async startBroadcastingAudioStream(mediaStreamTrack: MediaStreamTrack): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );

    if (!localTrackPublication || localTrackPublication.track?.id !== mediaStreamTrack.id) {
      this.publishAndEnableBroadcastingAudioStream(mediaStreamTrack);
      return;
    }

    await localTrackPublication.unmute();
  }

  async stopBroadcastingVideoStream(): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Camera,
    );

    if (!localTrackPublication) {
      return;
    }

    await localTrackPublication.mute();
  }

  async stopBroadcastingAudioStream(): Promise<void> {
    const localTrackPublication = this.room.localParticipant.getTrackPublication(
      Track.Source.Microphone,
    );

    if (!localTrackPublication) {
      return;
    }

    await localTrackPublication.mute();
  }

  async setStreamQuality(
    participantId: string,
    type: TrackType,
    quality: SubscribeCallQuality,
  ): Promise<void> {
    const remoteTrackPublication = this.room
      .getParticipantByIdentity(participantId)
      ?.getTrackPublication(
        type === TrackType.VIDEO ? Track.Source.Camera : Track.Source.ScreenShare,
      ) as RemoteTrackPublication;

    if (!remoteTrackPublication) {
      return;
    }

    remoteTrackPublication.setVideoQuality(quality as unknown as VideoQuality);
  }

  isCameraEnabled(): boolean {
    return this.room.localParticipant.isCameraEnabled;
  }

  isMicEnabled(): boolean {
    return this.room.localParticipant.isMicrophoneEnabled;
  }

  public subscribe(participantId: string, trackType: TrackType): void {
    this._subscribe(participantId, trackType, true);
  }

  public unsubscribe(participantId: string, trackType: TrackType): void {
    this._subscribe(participantId, trackType, false);
  }

  public stage(participantId: string, trackType: TrackType): void {
    this._subscribe(participantId, trackType, false);
  }

  async startScreenShare(
    options?: ScreenShareOptions,
    onEnd?: () => Promise<void>,
    onError?: (error: any) => Promise<void>,
  ): Promise<any> {
    if (!this.room) {
      return;
    }
    if (onEnd) {
      this.onEndScreenSharing = onEnd;
    }
    const preferCurrentTab = options?.preferCurrentTab ?? false;
    const track = await this.room.localParticipant
      // Used featue flag variables directly as we're sure at this moment, user should have the correct values
      .setScreenShareEnabled(
        true,
        {
          audio: !preferCurrentTab,
          preferCurrentTab,
          // note: There is a bug in the element capture API that does not let you "unrestrict" the screenshare
          // once the screenshare is not the current tab so we will disable moving switching from current tab
          surfaceSwitching: options?.preferCurrentTab ? 'exclude' : 'include',
          resolution: {
            width: this.screenShareCaptureWidth,
            height: this.screenShareCaptureHeight,
            frameRate: this.screenShareCaptureMaxFPS,
          },
        },
        {
          videoCodec: this.screenShareEncodingCodec as VideoCodec,
          simulcast: this.isScreenShareSimulcastEnabled,
          name: options?.preferCurrentTab ? HIDE_TRACK_SYMBOL : undefined,
          screenShareEncoding: {
            maxBitrate: this.screenShareEncodingMaxBitrate,
            maxFramerate: this.screenShareEncodingMaxFPS,
            priority: 'medium',
          },
        },
      )
      .catch((error) => {
        if (onError) {
          onError(error);
        }
      });

    if (options?.restrictToWhiteboard && track?.track?.mediaStreamTrack) {
      await this.setupWhiteboardRestrictionListener(track.track.mediaStreamTrack);
    }
    return track;
  }

  attachAudioElement(
    participant: CallParticipant,
    trackType: TrackType,
    element: HTMLAudioElement,
  ): void {
    const liveKitParticipant = this.room.getParticipantByIdentity(participant.participantId);
    if (trackType === TrackType.VIDEO) {
      liveKitParticipant?.getTrackPublication(Track.Source.Microphone)?.audioTrack?.attach(element);
    } else if (trackType === TrackType.SCREEN) {
      liveKitParticipant
        ?.getTrackPublication(Track.Source.ScreenShareAudio)
        ?.audioTrack?.attach(element);
    }
  }

  async stopScreenShare(): Promise<any> {
    if (!this.room) {
      return;
    }
    return this.room.localParticipant.setScreenShareEnabled(false);
  }

  async startRecording(isAutomaticTrigger = false): Promise<void> {
    // Clear any existing retry attempts if this is a manual call
    if (!isAutomaticTrigger) {
      if (this.startRecordingRetryTimeout) {
        clearTimeout(this.startRecordingRetryTimeout);
        this.startRecordingRetryTimeout = undefined;
      }
      this.startRecordingRetryAttempt = 0;
    }

    this.startOrStopRecordingMutex.runExclusive(async () => {
      if (!this.spaceRepo.activeSpaceId || !this.spaceRepo.activeSpaceCurrentRoomUid) {
        return;
      }

      try {
        const startEgressRequest = await firstValueFrom(
          this.spacesService.changeCloudRecordingState(
            CallProvider.LIVEKIT,
            this.spaceRepo.activeSpaceId,
            this.room.name,
            true,
            this.isParticipantEgressType ? this.room.localParticipant.identity : undefined,
          ),
        );

        if (startEgressRequest?.data?.success) {
          this.currentActiveEgressRequest = startEgressRequest?.data?.recordingId;
          // Reset retry attempt on success
          this.startRecordingRetryAttempt = 0;

          // For participant egress type, we need to handle setting the recording status here
          // since we're ignoring the RecordingStatusChanged event
          if (this.isParticipantEgressType) {
            this.setMetadata({ recordingInitiator: undefined });
            this.providerState.recordingEvent = {
              _type: 'STARTED',
              initiatorParticipantId: undefined,
            };
          }
        }

        // This is to cover a quick start recording request was done while stop recording request is in progress
        // It will result in LiveKit won't emit RecordingStatusChanged again given recording starts again. So, this condition to update the initiator side
        if (this.room.isRecording && !this.isParticipantEgressType) {
          this.setMetadata({ recordingInitiator: this.isParticipantEgressType ? undefined : true });
          this.providerState.recordingEvent = {
            _type: 'STARTED',
            initiatorParticipantId: this.currentActiveEgressRequest
              ? this.room.localParticipant.identity
              : undefined,
          };
        }
      } catch (startRecordingError) {
        // @TODO [Elwakeel] add UX for failing start recording requests, also the same for stop recording requests
        captureException(startRecordingError);

        // Only attempt retry if this was an automatic trigger
        // and we haven't exceeded max retries
        if (
          isAutomaticTrigger &&
          this.startRecordingRetryAttempt < this.MAX_START_RECORDING_RETRIES
        ) {
          this.retryStartRecording();
        }
      }
    });
  }

  private retryStartRecording(): void {
    // Calculate delay with exponential backoff
    const delay = this.INITIAL_RETRY_DELAY * Math.pow(2, this.startRecordingRetryAttempt);

    this.logMeetingEvent('retrying_start_recording', {
      attempt: this.startRecordingRetryAttempt + 1,
      delay,
    });

    this.startRecordingRetryTimeout = modifiedSetTimeout(
      () => {
        this.startRecordingRetryAttempt++;
        this.startRecording(true); // Pass true to indicate this is an automatic retry
      },
      delay,
      true,
    ); // run outside zone
  }

  private handleParticipantEgressTrigger(): void {
    if (this.isParticipantEgressType) {
      this.startRecordingRetryAttempt = 0;
      this.startRecording(true); // Pass true to indicate this is an automatic trigger
    }
  }

  async stopRecording(): Promise<void> {
    this.startOrStopRecordingMutex.runExclusive(async () => {
      if (
        !this.spaceRepo.activeSpaceId ||
        !this.spaceRepo.activeSpaceCurrentRoomUid ||
        (!this.room.isRecording && !this.room.remoteParticipants.get(PENCIL_BOT_IDENTITY))
      ) {
        return;
      }

      try {
        const stopResponse = await firstValueFrom(
          this.spacesService.changeCloudRecordingState(
            CallProvider.LIVEKIT,
            this.spaceRepo.activeSpaceId,
            this.room.name,
            false,
            this.isParticipantEgressType ? this.room.localParticipant.identity : undefined,
            this.currentActiveEgressRequest,
          ),
        );
        // Make sure recordingIitiator is empty to cover the case of multiple CR toggles within the same call session
        this.setMetadata({ recordingInitiator: undefined });
        this.currentActiveEgressRequest = undefined;

        // For participant egress type, we need to handle setting the recording status here
        // since we're ignoring the RecordingStatusChanged event
        if (this.isParticipantEgressType && stopResponse?.data?.success) {
          this.providerState.recordingEvent = {
            _type: 'STOPPED',
          };
        }
      } catch (stopRecordingError) {
        captureException(stopRecordingError);
      }
    });
  }

  setLocalVideoCallStatsMetadata(userSystemStatus: UserSystemStatus): void {
    // Emit local metadata change before sending to server
    this.emitLocalParticipantMetadataChange(userSystemStatus);
    this.setMetadata(userSystemStatus);
  }

  toCallParticipant(participant: Participant): CallParticipant {
    return {
      participantId: participant.identity,
      screenShareTrackSid: participant.getTrackPublication(Track.Source.ScreenShare)?.trackSid,
      userId: participant.identity.split('$')[0],
      video: this.getTrackState(participant.getTrackPublication(Track.Source.Camera)),
      audio: this.getTrackState(participant.getTrackPublication(Track.Source.Microphone)),
      screen: this.getTrackState(participant.getTrackPublication(Track.Source.ScreenShare)),
      screenAudio: this.getTrackState(
        participant.getTrackPublication(Track.Source.ScreenShareAudio),
      ),
      videoTrack: participant.getTrackPublication(Track.Source.Camera)?.videoTrack
        ?.mediaStreamTrack,
      audioTrack: participant.getTrackPublication(Track.Source.Microphone)?.audioTrack
        ?.mediaStreamTrack,
      screenVideoTrack: participant.getTrackPublication(Track.Source.ScreenShare)?.videoTrack
        ?.mediaStreamTrack,
      screenAudioTrack: participant.getTrackPublication(Track.Source.ScreenShareAudio)?.audioTrack
        ?.mediaStreamTrack,
      local: participant.isLocal,
      joinedAt: participant.joinedAt,
      userSystemStatus: this.parseMetadata(participant.metadata),
      name: participant.name,
      agentInfo: this.getAgentInfo(participant),
    };
  }

  private getTrackState(track: TrackPublication | undefined): TrackState {
    if (!track || track.isMuted) {
      return 'off';
    } else if (track.trackName === HIDE_TRACK_SYMBOL) {
      return 'hidden';
    } else {
      return 'playable';
    }
  }

  async setDevices(devices: ActiveRTCDevices): Promise<void> {
    const devicePromises: Promise<boolean>[] = [];
    if (devices.micId) {
      devicePromises.push(this.room.switchActiveDevice('audioinput', devices.micId));
    }
    if (devices.cameraId) {
      devicePromises.push(this.room.switchActiveDevice('videoinput', devices.cameraId));
    }
    if (devices.speakerId) {
      devicePromises.push(this.room.switchActiveDevice('audiooutput', devices.speakerId));
    }
    await Promise.all(devicePromises);
  }

  // No need for cam/mic as we are using our local tracks manager for broadcasting tracks
  getCurrentInputDevices(): Promise<ActiveRTCDevices> {
    const activeRTCDevices: ActiveRTCDevices = {
      speakerId: '',
    };

    activeRTCDevices.speakerId = this.room.getActiveDevice('audiooutput') || '';

    return Promise.resolve(activeRTCDevices);
  }

  doesProviderSupportNoiseCancellation(): boolean {
    // Check if the module is already loaded, it should be loaded inside the session module component
    if (this.dynamicModuleLoader.isModuleLoaded(DynamicModuleKey.KRISP_NOISE_FILTER)) {
      const krispModule = this.dynamicModuleLoader.getCachedModule(
        DynamicModuleKey.KRISP_NOISE_FILTER,
      );
      return krispModule?.isKrispNoiseFilterSupported() ?? false;
    }

    return false;
  }

  /**
   * Dynamically loads the Krisp noise filter module.
   * Uses DynamicModuleLoaderService for caching and error handling.
   */
  async loadVideoCallsModules(): Promise<any | null> {
    return this.dynamicModuleLoader.loadModule(DynamicModuleKey.KRISP_NOISE_FILTER);
  }

  /**
   * Checks if Krisp noise cancellation is actually supported.
   * Loads the module if needed.
   */
  private async checkKrispSupportAsync(): Promise<boolean> {
    const krispModule = await this.loadVideoCallsModules();

    if (!krispModule) {
      return false;
    }

    return krispModule.isKrispNoiseFilterSupported();
  }

  // Note: this does not include the local participant
  getParticipants(): { [key: string]: CallParticipant } {
    const livekitParticipants = this.room.remoteParticipants;
    const callParticipants: { [key: string]: CallParticipant } = {};
    livekitParticipants.forEach((value: RemoteParticipant, key: string) => {
      if (value.identity === PENCIL_BOT_IDENTITY || value.identity === HIDDEN_PENCIL_BOT_IDENTITY) {
        return;
      }
      // Hide the avatar principal — its video is rendered by the delegate tile.
      if (this.isAgentPrincipalWithDelegate(value)) {
        return;
      }
      callParticipants[key] = this.toCallParticipant(value);
    });
    return callParticipants;
  }

  getVideoDimensionsByIdentity(
    identity: string,
    trackType: TrackType,
  ): { width: number; height: number } {
    const participant = this.room.getParticipantByIdentity(identity);
    if (!participant) {
      return this._defaultVideoDimensions;
    }

    const trackPublication = participant.getTrackPublication(
      trackType === TrackType.VIDEO ? Track.Source.Camera : Track.Source.ScreenShare,
    );
    if (!trackPublication) {
      return this._defaultVideoDimensions;
    }

    return trackPublication.dimensions ?? this._defaultVideoDimensions;
  }

  getNumberOfParticipants(): number {
    let participantsCount = Object.values(this.getParticipants()).length;

    if (!this.observerModeService.isObserverModeActive()) {
      participantsCount++;
    }

    return participantsCount;
  }

  protected getLocalParticipant(): LocalParticipant | null {
    return this.room?.localParticipant ?? null;
  }

  // === Push-to-talk =========================================================

  private handleParticipantAttributesChange(
    _changed: Record<string, string>,
    participant: Participant,
  ): void {
    // Only the principal agent broadcasts PTT state. Mirror its full attribute
    // set so the button reflects the live mode + holder. The avatar delegate
    // (publish_on_behalf) carries no PTT attrs — ignore it so it can't clobber
    // the button. Fires outside the Angular zone (like every other room event
    // here) — the consumer re-enters the zone.
    if (participant.isAgent && !participant.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF]) {
      this._agentAttributes$.next({ ...participant.attributes });
    }
  }

  /** The avatar "delegate" participant (if any) publishing media on behalf of
   * `principalIdentity` (i.e. an out-of-process Tavus/bitHuman avatar). */
  private getAvatarDelegateFor(principalIdentity: string): RemoteParticipant | undefined {
    if (!this.room) {
      return undefined;
    }
    for (const p of this.room.remoteParticipants.values()) {
      if (p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF] === principalIdentity) {
        return p;
      }
    }
    return undefined;
  }

  /** True for an agent "principal" whose video is published by a separate
   * avatar delegate participant. Such a principal has no media of its own, so
   * we hide its tile and let the delegate render as the single agent tile. */
  private isAgentPrincipalWithDelegate(participant: Participant): boolean {
    return (
      !!participant?.isAgent &&
      !participant.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF] &&
      !!this.getAvatarDelegateFor(participant.identity)
    );
  }

  /** The agent/bot participant in the room (kind=AGENT), if present. When an
   * avatar delegate is present we return the PRINCIPAL (the brain that
   * broadcasts PTT state), never the delegate (which carries no PTT attrs). */
  getAgentParticipant(): RemoteParticipant | undefined {
    if (!this.room) {
      return undefined;
    }
    let principal: RemoteParticipant | undefined;
    let anyAgent: RemoteParticipant | undefined;
    for (const p of this.room.remoteParticipants.values()) {
      if (!p.isAgent) {
        continue;
      }
      anyAgent = anyAgent ?? p;
      // The principal is the agent that is NOT an avatar delegate.
      if (!p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF]) {
        principal = principal ?? p;
      }
    }
    return principal ?? anyAgent ?? this.room.remoteParticipants.get(PENCIL_BOT_IDENTITY);
  }

  /** Recompute agentInCall$ from the live room roster. True iff a PRINCIPAL voice
   * agent (kind=AGENT, not the avatar delegate) is connected. Deliberately does NOT
   * use getAgentParticipant()'s PENCIL_BOT fallback — the buttons track the voice
   * worker specifically, not a persistent bot identity. Idempotent (only emits on a
   * real change), so it's safe to call on every participant event. */
  private recomputeAgentInCall(): void {
    const present =
      !!this.room &&
      Array.from(this.room.remoteParticipants.values()).some(
        (p) => p.isAgent && !p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF],
      );
    if (present !== this._agentInCall$.value) {
      this._agentInCall$.next(present);
    }
    // Same roster pass: is an avatar delegate here? Keyed on the delegate's
    // `lk.publish_on_behalf` rather than on any video track, so the answer does
    // not flap while the avatar's video is still connecting (or, as on the
    // floating-call layout, never gets subscribed at all).
    const avatar =
      !!this.room &&
      Array.from(this.room.remoteParticipants.values()).some(
        (p) => !!p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF],
      );
    if (avatar !== this._agentAvatarPresent$.value) {
      this._agentAvatarPresent$.next(avatar);
    }
  }

  /** Latest PTT attributes the agent has broadcast (mode/holder). */
  getAgentAttributes(): Record<string, string> {
    return this.getAgentParticipant()?.attributes ?? {};
  }

  /**
   * Invoke an RPC method on the agent participant (e.g. `pencil.ptt`). Returns
   * the agent's string response, or null when no agent is present. Used by
   * push-to-talk to start/end a hold.
   */
  async performAgentRpc(method: string, payload: Record<string, unknown>): Promise<string | null> {
    const agent = this.getAgentParticipant();
    const local = this.room?.localParticipant;
    if (!agent || !local) {
      return null;
    }
    return local.performRpc({
      destinationIdentity: agent.identity,
      method,
      payload: JSON.stringify(payload),
    });
  }

  // Events handlers

  // === Local participant events handlers ===

  // Handle only connecting event as other events are handled as explicit RoomEvent
  private handleConnectionStateChanged(connectionState: ConnectionState) {
    if (connectionState === ConnectionState.Connecting) {
      this.providerState.callConnecting = true;
    }
  }

  private handleCallIsConnected() {
    // setting the speaker id before joining the call not working if the speaker not the default
    this.forceSettingSpeakerDevice();
    this.providerState.callConnecting = false;
    this.providerState.callConnected = true;
    this.currentLocalParticipant = cloneDeep(this.room.localParticipant);
    this.broadcastLocalParticipant(true);
    this.broadcastLocalParticipantId(this.room.localParticipant);
    this.broadcastExistingParticipantEvents();
    if (this.room.isRecording) {
      this.handleRecordingStatusChange(true, RecordingStatusSource.LIVEKIT);
    }
    this.checkRecordingStatusWithDelay();
    this.handleParticipantEgressTrigger();
    // Voice is user-driven via the AI-panel call-join button (CALL_JOIN_BUTTONS_SPEC.md),
    // unless the organization has "Join calls automatically" on. Ask pencil-api, which
    // checks that setting and sends the agent in once per call; nothing happens
    // otherwise. Main room only: that is the call the agent joins.
    const spaceId = this.spaceRepo.activeSpaceId;
    if (
      spaceId &&
      this.room.name === `${spaceId}-MainRoom` &&
      this.spaceRepo.activeSpace?.institution?.aiAgentEnabled &&
      !this.observerModeService.isObserverModeActive()
    ) {
      this.aiSessionService.autoJoinAgentCall();
    }
    // Seed the button state from the roster: an agent may already be in the room
    // (dispatched by another user before this human joined) → show red immediately.
    this.recomputeAgentInCall();
    // Same for the agent's attributes (lk.agent.state drives the orb, the PTT keys the
    // button): ParticipantConnected fires only for agents that join AFTER us, so an
    // agent already here would otherwise show "loading" until its next state change.
    const agent = Array.from(this.room.remoteParticipants.values()).find(
      (p) => p.isAgent && !p.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF],
    );
    if (agent) {
      this._agentAttributes$.next({ ...agent.attributes });
    }
  }

  private handleReconnectingEvent() {
    this.providerState.callReconnecting = true;
  }

  private handleReconnectedEvent() {
    this.providerState.callReconnecting = false;
  }

  private handleConnectionQualityChanged(
    connectionQuality: ConnectionQuality,
    participant: Participant,
  ) {
    if (
      !participant.isLocal ||
      !this.providerState.callReconnectingValue ||
      ![ConnectionQuality.Excellent, ConnectionQuality.Good].includes(connectionQuality)
    ) {
      return;
    }
    // This is as a backup for reconnecting notification as we figured some cases that Reconnected event didn't be fired correctly
    this.providerState.callReconnecting = false;
  }

  private handleDisconnectedEvent(reason?: DisconnectReason | undefined) {
    // Only return if the disconnection happened while connecting to a call due to user rejected error (ConnectionErrorReason.NotAllowed)
    if (reason === DisconnectReason.USER_REJECTED && this.providerState.callConnecting) {
      return;
    }

    if (this.recordingStatusCheckTimeout) {
      clearTimeout(this.recordingStatusCheckTimeout);
      this.recordingStatusCheckTimeout = undefined;
    }

    // No room → no agent in call; reset the button state so it doesn't stick on "red".
    this._agentInCall$.next(false);

    this.providerState.callConnecting = false;
    this.providerState.callDisconnecting = false;
    this.providerState.callReconnecting = false;
    this.providerState.participantEvents = null;
    this.providerState.trackEvents = null;
    this.providerState.callConnected = false;
    this.providerState.recordingEvent = {
      _type: 'STOPPED',
    };
    this.activeRecordingSources.clear();
    this.broadcastLocalParticipant(false);
    this.currentLocalParticipant = undefined;
    this.pendingSubscriptions = {};
    this.currentActiveEgressRequest = undefined;
    this.egressCheckAttempts = 0;
    this.dismissVideoCallNotifications();
    this.krispProcessor = undefined;

    switch (reason) {
      // We are handling Disconnected event using its explicit event (Participant left)
      case DisconnectReason.CLIENT_INITIATED:
        break;
      case DisconnectReason.SERVER_SHUTDOWN:
        this.providerState.callErrors = CallError.SERVICEDOWN;
        break;
      case DisconnectReason.JOIN_FAILURE:
        this.providerState.callErrors = CallError.TIME_OUT;
        break;
      default:
        this.providerState.callErrors = CallError.DISCONNECTED;
    }
  }

  private async handleLocalTrackEvent(
    pub: LocalTrackPublication,
    participant: Participant,
  ): Promise<void> {
    // Reflect the correct Noise Cancellation setting once local track is published as per LiveKit documentation
    // Link: https://docs.livekit.io/guides/enhanced-noise-cancellation/
    if (
      pub.source === Track.Source.Microphone &&
      pub.track instanceof LocalAudioTrack &&
      !this.isMusicModeEnabled
    ) {
      if (this.isNoiseCancellationEnabled) {
        this.enableNoiseCancellation();
      } else {
        this.disableNoiseCancellation();
      }
    }

    // Don't play local tracks if publication is muted
    if (pub.isMuted) {
      return;
    }

    this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    this.publishTrackEvent(TrackEventAction.START, participant, pub);
  }

  private handleLocalTrackUnpubEvent(pub: TrackPublication, participant: Participant): void {
    this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  // === Remote participants events handlers ===

  private handleParticipantJoinEvent(participant: Participant): void {
    // If the participant is an agent and the flag is not enabled, we skip adding it
    if (participant.isAgent && !this.flagsService.isFlagEnabled(FLAGS.ENABLE_AGENT_JOIN_CALL)) {
      return;
    }

    // When an avatar delegate joins, its principal's (media-less) tile — created
    // moments earlier when the principal joined alone — is now redundant. Tear
    // it down so only the delegate's video tile remains.
    const behalf = participant.attributes?.[ATTRIBUTE_PUBLISH_ON_BEHALF];
    if (behalf) {
      const principal = this.room.getParticipantByIdentity(behalf);
      if (principal) {
        this.publishParticipantEvent(ParticipantEventAction.LEFT, principal);
      }
    }

    this.handleParticipantMetadataChange(participant.metadata, participant);
    this.publishParticipantEvent(ParticipantEventAction.JOINED, participant);
  }

  private handleParticipantLeaveEvent(participant: Participant): void {
    this.publishParticipantEvent(ParticipantEventAction.LEFT, participant);

    // NOTE: when an avatar delegate leaves we deliberately do NOT restore the
    // principal's tile. The principal carries no media of its own (audio rides
    // the avatar in echo mode), so restoring it shows a dead, frozen tile. The
    // worker auto-reattaches a fresh avatar within seconds (worker.py watchdog);
    // its new delegate brings the tile back. A brief no-tile gap beats a dead
    // tile, and if reattach ultimately fails the agent is honestly absent.
  }

  // === Track events handlers ===

  private handleTrackPubEvent(pub: RemoteTrackPublication, participant: RemoteParticipant): void {
    // Broadcast participant updated event on each new publication to make participants manager list up-to-date
    this.publishParticipantEvent(ParticipantEventAction.UPDATED, participant);
    if (isEmpty(this.pendingSubscriptions)) {
      return;
    }

    const pendingSubscriptionId = participant.identity + pub.source;

    if (
      pub.isSubscribed ||
      this.pendingSubscriptions[pendingSubscriptionId] === undefined ||
      this.pendingSubscriptions[pendingSubscriptionId] !== PendingSubscription.SUBSCRIBE
    ) {
      return;
    }

    pub.setSubscribed(true);
  }

  private handleTrackUnpubEvent(pub: TrackPublication, participant: Participant): void {
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  private handleTrackUnsubEvent(
    event: Track,
    pub: TrackPublication,
    participant: Participant,
  ): void {
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  private handleTrackSubscribedEvent(
    event: Track,
    pub: TrackPublication,
    participant: Participant,
  ): void {
    this.broadcastUnMutedSubscribedEvents(pub, participant);
  }

  private handleTrackMutedEvent(pub: TrackPublication, participant: Participant): void {
    this.publishTrackEvent(TrackEventAction.STOP, participant, pub);
  }

  private handleTrackUnMutedEvent(pub: TrackPublication, participant: Participant): void {
    this.publishTrackEvent(TrackEventAction.START, participant, pub);
  }

  // We are just using Participant metadata for processing cloud recording start
  // We needed to handle it using this way as LiveKit doesn't support an explicit way to broadcast recording initiator
  private handleParticipantMetadataChange(metadata: string | undefined, participant: Participant) {
    if (
      participant.identity === PENCIL_BOT_IDENTITY ||
      participant.identity === HIDDEN_PENCIL_BOT_IDENTITY
    ) {
      return;
    }
    // Hidden avatar principal — don't resurrect its tile via a metadata UPDATE.
    if (this.isAgentPrincipalWithDelegate(participant)) {
      return;
    }
    try {
      // Process remote participants metadata changes only
      if (participant.isLocal) {
        return;
      }

      const newParticipantMetadata = this.parseMetadata(participant.metadata);

      this.providerState.participantMetadataChange = this.toCallParticipant(participant);

      // Check if metadata update is related to cloud recording
      if (
        this.providerState.recordingEvent?._type !== 'STARTED' &&
        !!newParticipantMetadata.recordingInitiator === true
      ) {
        this.providerState.recordingEvent = {
          _type: 'STARTED',
          initiatorParticipantId: participant.identity,
        };
      }
    } catch (error) {
      captureException(error);
    }
  }

  private handleRecordingStatusChange(recording: boolean, source: RecordingStatusSource): void {
    // Skip the logic if we're using participant egress type
    // For participant egress type, recording state will be handled directly in the
    // startRecording and stopRecording methods based on API response success
    if (this.isParticipantEgressType) {
      return;
    }

    if (recording) {
      // Mark this source as actively recording.
      this.activeRecordingSources.add(source);
    } else {
      // Mark this source as no longer actively recording.
      this.activeRecordingSources.delete(source);

      // If there is still at least one active source (e.g. LiveKit or Pencil bot),
      // we should not treat this as a global "stop".
      if (this.activeRecordingSources.size > 0) {
        return;
      }
    }

    // Update Room metadata to let other users know there is an active recording
    // Decided to do it on the client side as receving a recording status change with true means recording actually starts
    if (recording && this.currentActiveEgressRequest) {
      this.setMetadata({ recordingInitiator: this.isParticipantEgressType ? undefined : true });
    }

    if (recording) {
      this.providerState.recordingEvent = {
        _type: 'STARTED',
        initiatorParticipantId: this.currentActiveEgressRequest
          ? this.room.localParticipant.identity
          : undefined,
      };
      return;
    }

    this.currentActiveEgressRequest = undefined;
    this.providerState.recordingEvent = {
      _type: 'STOPPED',
    };
  }

  // Helper functions

  private async reuseExistingLocalTrackPub(
    source: Track.Source,
    mediaStreamTrack: MediaStreamTrack,
  ): Promise<boolean> {
    const localTrackPub = this.room.localParticipant.getTrackPublication(source);
    const localTrack =
      source === Track.Source.Camera ? localTrackPub?.videoTrack : localTrackPub?.audioTrack;

    if (!localTrack || !localTrackPub) {
      return false;
    }

    await localTrack.replaceTrack(mediaStreamTrack, {
      userProvidedTrack: true,
    });
    localTrack.unmute();

    // Simulate stop & start on local side as local track publication isn't working like the remote one
    this.rebroadcastLocalTrack(localTrackPub);

    return true;
  }

  // It's mainly used to reflect the local track if it's changed/processed
  private rebroadcastLocalTrack(localTrackPublication: LocalTrackPublication) {
    this.publishTrackEvent(
      TrackEventAction.STOP,
      this.room.localParticipant,
      localTrackPublication,
    );
    this.publishTrackEvent(
      TrackEventAction.START,
      this.room.localParticipant,
      localTrackPublication,
    );
  }

  private broadcastUnMutedSubscribedEvents(pub: TrackPublication, participant: Participant) {
    /**
     * To cover all already active tracks after joining the call
     * Also it's used to start any track event
     * */
    if (!pub.isMuted) {
      this.publishTrackEvent(TrackEventAction.START, participant, pub);
    }
  }

  private broadcastLocalParticipantId(participant: Participant) {
    this.providerState.localParticipantId = participant.identity;
  }

  private publishParticipantEvent(action: ParticipantEventAction, participant: Participant) {
    const skipLocalObserverEvent = isObserverParticipant(participant);
    if (participant.identity === HIDDEN_PENCIL_BOT_IDENTITY || skipLocalObserverEvent) {
      // Hidden bot should not appear in UI or affect recording state
      return;
    }

    if (participant.identity === PENCIL_BOT_IDENTITY) {
      if (action === ParticipantEventAction.JOINED) {
        this.handleRecordingStatusChange(true, RecordingStatusSource.PENCIL_BOT);
      } else if (action === ParticipantEventAction.LEFT) {
        this.handleRecordingStatusChange(false, RecordingStatusSource.PENCIL_BOT);
      }
      return;
    }
    // Avatar principal: suppress its (media-less) tile. LEFT is allowed through
    // so an already-shown principal tile can be torn down once its delegate
    // arrives (see handleParticipantJoinEvent).
    if (action !== ParticipantEventAction.LEFT && this.isAgentPrincipalWithDelegate(participant)) {
      return;
    }
    const participantEvent = {
      action: action,
      participant: this.toCallParticipant(participant),
    };
    this.providerState.participantEvents = participantEvent;
  }

  private publishTrackEvent(
    action: TrackEventAction,
    liveKitParticipant: Participant,
    publication: TrackPublication,
  ): void {
    if (this.getTrackType(publication.source) === TrackType.SCREEN) {
      this.publishParticipantEvent(ParticipantEventAction.UPDATED, liveKitParticipant);

      // Handle reflecting the correct UI after stopping sharing from the bottom toolbar
      // Only for local participant if he owns this track stopped event for screen sharing
      if (
        liveKitParticipant.isLocal &&
        action === TrackEventAction.STOP &&
        this.onEndScreenSharing
      ) {
        this.onEndScreenSharing();
      }
    }
    const trackEvent = {
      action: action,
      participant: this.toCallParticipant(liveKitParticipant),
      type: this.getTrackType(publication.source),
    };
    this.providerState.trackEvents = trackEvent;
    if (liveKitParticipant.isLocal) {
      this.publishParticipantEvent(ParticipantEventAction.UPDATED, liveKitParticipant);
    }
  }

  private getTrackType(source: Track.Source): TrackType {
    switch (source) {
      case Track.Source.Camera:
        return TrackType.VIDEO;
      case Track.Source.Microphone:
        return TrackType.AUDIO;
      case Track.Source.ScreenShare:
        return TrackType.SCREEN;
      case Track.Source.ScreenShareAudio:
        return TrackType.SCREEN_AUDIO;
      default:
        return TrackType.VIDEO;
    }
  }

  private async forceSettingSpeakerDevice() {
    if (!this.deviceAndBrowserDetectorService.isSetSinkIdSupported()) {
      return;
    }
    const speakerId = this.getSelectedSpeakerDeviceId() || DEFAULT_DEVICE_ID;
    try {
      await this.room.switchActiveDevice('audiooutput', speakerId);
    } catch (error) {
      console.error('failed to switch audio output when joining the call', error);
    }
  }

  private broadcastExistingParticipantEvents(): void {
    const participants = this.getParticipants();
    Object.entries(participants).forEach(([_, participant]) => {
      // To cover the case of a CR is already started before local user's join as LiveKit isn't broadcasting any event happened before your join
      const liveKitParticipant = this.room.getParticipantByIdentity(participant.participantId);

      // If the participant is an agent and the flag is not enabled, we skip adding it
      if (
        liveKitParticipant?.isAgent &&
        !this.flagsService.isFlagEnabled(FLAGS.ENABLE_AGENT_JOIN_CALL)
      ) {
        return;
      }

      this.providerState.participantEvents = {
        action: ParticipantEventAction.JOINED,
        participant,
      };

      if (liveKitParticipant) {
        this.handleParticipantMetadataChange(liveKitParticipant.metadata, liveKitParticipant);
      }
    });
    this.updateRecordingStatusFromPencilBotPresence();
  }
  private updateRecordingStatusFromPencilBotPresence(): void {
    this.room.remoteParticipants.forEach((participant) => {
      if (participant.identity === PENCIL_BOT_IDENTITY) {
        this.handleRecordingStatusChange(true, RecordingStatusSource.PENCIL_BOT);
        return;
      }
    });
  }

  private broadcastLocalParticipant(isJoined: boolean) {
    this.providerState.participantEvents = {
      action: isJoined ? ParticipantEventAction.JOINED : ParticipantEventAction.LEFT,
      participant: this.toCallParticipant(
        isJoined
          ? this.room.localParticipant
          : this.currentLocalParticipant ?? this.room.localParticipant,
      ),
    };
  }

  private _subscribe(id: string, trackType: TrackType, enabled: boolean): void {
    let source: Track.Source;
    switch (trackType) {
      case TrackType.AUDIO:
        source = Track.Source.Microphone;
        break;
      case TrackType.VIDEO:
        source = Track.Source.Camera;
        break;
      case TrackType.SCREEN:
        source = Track.Source.ScreenShare;
        break;
      case TrackType.SCREEN_AUDIO:
        source = Track.Source.ScreenShareAudio;
        break;
      default:
        throw new Error('Track type not supported');
    }
    const remoteParticipant = this.room.getParticipantByIdentity(id);
    const remoteTrackPublication = remoteParticipant?.getTrackPublication(
      source,
    ) as RemoteTrackPublication;
    if (remoteTrackPublication) {
      if (
        !this.flagsService.isFlagEnabled(FLAGS.USE_SET_ENABLED_FOR_VIDEO_TRACKS) ||
        (enabled && !remoteTrackPublication.isSubscribed)
      ) {
        // first subscribe
        remoteTrackPublication.setSubscribed(enabled);
      }
      if (this.flagsService.isFlagEnabled(FLAGS.USE_SET_ENABLED_FOR_VIDEO_TRACKS)) {
        if (remoteTrackPublication.isEnabled === enabled) {
          return;
        }
        remoteTrackPublication.setEnabled(enabled);
        if (enabled) {
          this.broadcastUnMutedSubscribedEvents(remoteTrackPublication, remoteParticipant!);
        } else {
          this.handleTrackMutedEvent(remoteTrackPublication, remoteParticipant!);
        }
      }
    }

    // Sometimes remote publications take some time to be sent to remote participants
    // In such case we will maintain a map for all requested subscriptions that can't find available remote publication
    // Then do the subscription on receving the remote track publication
    if (enabled) {
      this.pendingSubscriptions[id + source] = PendingSubscription.SUBSCRIBE;
    } else {
      delete this.pendingSubscriptions[id + source];
    }
  }

  // ---- Metadata ----

  private setMetadata(nextMetadata: LivekitParticipantMetadata) {
    const currentMetadataString = this.room.localParticipant.metadata;
    if (!currentMetadataString) {
      this.room.localParticipant.setMetadata(JSON.stringify(nextMetadata)).catch(() => {
        // be silent, it's not critical if it fails
      });
    } else {
      const currentMetadata: LivekitParticipantMetadata = JSON.parse(currentMetadataString);
      const updatedMetadata = JSON.stringify({ ...currentMetadata, ...nextMetadata });
      this.room.localParticipant.setMetadata(updatedMetadata).catch(() => {
        // be silent, it's not critical if it fails
      });
    }
  }

  private parseMetadata(metadata: string | undefined): LivekitParticipantMetadata {
    return metadata ? JSON.parse(metadata) : {};
  }

  private updateMetadata$(): Observable<LivekitParticipantMetadata> {
    return fromEvent(this.room, RoomEvent.Connected).pipe(
      switchMap(() => this.getParticipantMetadata$()),
    );
  }

  private getParticipantMetadata$(): Observable<LivekitParticipantMetadata> {
    return combineLatest([
      this.spaceRepo.isCurrentUserHost$,
      this.spaceRecordingService.isPrivacyAwareCloudRecordingEnabled$,
    ]).pipe(
      map(([isUserHost, isPrivacyAwareCloudRecordingEnabled]) => {
        // Determine if participant video should be recorded
        let shouldRecordParticipantVideo: boolean;
        if (!isPrivacyAwareCloudRecordingEnabled) {
          shouldRecordParticipantVideo = true;
        } else {
          // If privacy feature is enabled, only hosts' video should be recorded
          shouldRecordParticipantVideo = isUserHost;
        }

        // Always update metadata with isHost, userId, and tabId regardless of recording logic
        const userId = this.userService.userId;
        const tabId = this.userService.userUniqueHash;

        return {
          isHost: isUserHost,
          userId,
          tabId,
          shouldRecordParticipantVideo,
        };
      }),
      distinctUntilChanged(
        (prev, curr) =>
          prev.isHost === curr.isHost &&
          prev.userId === curr.userId &&
          prev.tabId === curr.tabId &&
          prev.shouldRecordParticipantVideo === curr.shouldRecordParticipantVideo,
      ),
      takeWhile(() => this.isConnected()),
    );
  }

  private logMeetingEvent(action: string, payload: Record<string, any> = {}): void {
    this.telemetry.event('[Meeting Event]', {
      action,
      ...payload,
    });
  }

  private shouldRecoverFromBrokenRecordingState(participant: Participant): boolean {
    return (
      participant.isLocal &&
      this.spaceRecordingService.isAutomaticCloudRecordingEnabled &&
      this.providerState.recordingEvent?._type !== 'STARTED' &&
      this.egressCheckAttempts < this.MAX_EGRESS_CHECKS
    );
  }

  private getEgressStatus(): Observable<{ items: Array<{ status: string }> } | null> {
    return this.http
      .post<{ items: Array<{ status: string }> }>(
        `${LIVEKIT_HOST}${LIVEKIT_EGRESS_PATH}`,
        { roomName: this.roomAuth?.roomId },
        {
          headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${this.roomAuth?.token}`,
          })
            .set(ErrorInterceptorSkipHeader, '')
            .set(InterceptorSkipHeader, ''),
        },
      )
      .pipe(
        catchError((error) => {
          captureException(new Error('Failed to check egress status'), {
            extra: { error },
          });
          return of(null);
        }),
      );
  }

  private checkRecordingStatusWithDelay(): void {
    this.recordingStatusCheckTimeout = modifiedSetTimeout(
      () => {
        if (this.shouldRecoverFromBrokenRecordingState(this.room.localParticipant)) {
          this.syncLivekitRecordingStatus();
        }
      },
      RECORDING_STATUS_CHECK_DELAY,
      true, // run outside zone
    );
  }

  private async syncLivekitRecordingStatus(): Promise<void> {
    this.logMeetingEvent('recovering_from_broken_recording_state', {
      room_recording_state: this.room.isRecording,
    });

    const response = await firstValueFrom(this.getEgressStatus());

    if (response?.items) {
      const hasActiveEgress = response.items.some(
        (egress) => egress.status === 'EGRESS_ACTIVE' || egress.status === 'EGRESS_STARTING',
      );
      if (hasActiveEgress) {
        this.handleRecordingStatusChange(true, RecordingStatusSource.LIVEKIT);
      } else {
        this.logMeetingEvent('no_active_egress_found', {
          attempt_number: this.egressCheckAttempts + 1,
        });
        this.checkRecordingStatusWithDelay();
      }
    }

    this.egressCheckAttempts++;
  }

  private get isParticipantEgressType(): boolean {
    return (
      this.spaceRepo.activeSpace?.institution?.settings?.crAdvancedSettings?.egressType ===
      Collaboration.Spaces.EgressType.PARTICIPANT
    );
  }

  // Not supported methods

  // Deprecated: It was used before with the old service for managing tracks
  isSubscribed!: (participantId: string, trackType: TrackType) => string | boolean;

  async preJoin(): Promise<void> {
    return;
  }
  startTranscription() {
    console.warn('transcription is not supported with livekit');
  }
  stopTranscription() {
    console.warn('transcription is not supported with livekit');
  }
  lowerOutgoingVideoQuality(): void {
    console.warn('[TO BE IMPLEMENTED] :lowerIncomingVideoQuality');
  }
  resetOutgoingVideoQuality(): void {
    console.warn('[TO BE IMPLEMENTED] :resetIncomingVideoQuality');
  }

  private getAgentInfo(participant: Participant): AgentInfo | undefined {
    return participant.isAgent
      ? {
          type: 'sparky',
          name: 'Sparky',
        }
      : undefined;
  }
}

type LivekitParticipantMetadata = Partial<
  {
    recordingInitiator: boolean;
    shouldRecordParticipantVideo: boolean;
    isHost: boolean;
    userId: string;
    tabId: string;
  } & UserSystemStatus
>;
